import { useState } from "react";
import { createPortal } from "react-dom";
import { Eye, Mail, Plus, Printer, Save, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { adminApi, czk, errMsg, fmtDate } from "../lib/api";
import { VoucherCard } from "../components/VoucherCard";
import { Field, Toggle, useList } from "./ui";

const PrintModal = ({ v, onClose }) => createPortal(
  <div className="fixed inset-0 z-[80] grid place-items-center overflow-y-auto bg-[#2D3426]/70 p-4" onClick={onClose} data-testid="voucher-print-modal">
    <div className="w-full max-w-4xl rounded-3xl bg-white p-5 sm:p-8" onClick={(e) => e.stopPropagation()}>
      <div className="no-print mb-5 flex items-center justify-between gap-3">
        <p className="h-section text-xl">Náhled k tisku</p>
        <div className="flex gap-2">
          <button className="btn-sage" onClick={() => window.print()} data-testid="voucher-print-btn"><Printer className="h-4 w-4" /> Vytisknout</button>
          <button className="rounded-full p-2 hover:bg-[#F7F3EA]" onClick={onClose} data-testid="voucher-print-close"><X className="h-5 w-5" /></button>
        </div>
      </div>
      <div className="print-area"><VoucherCard value={v.value} code={v.code} validUntil={v.valid_until} testid="voucher-print-card" /></div>
    </div>
  </div>, document.body);

const Row = ({ v, i, patch, reload, preview }) => {
  const save = async () => {
    try {
      await adminApi.put(`/vouchers/${v.id}`, v);
      toast.success("Uloženo");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const remove = async () => {
    if (!window.confirm("Opravdu smazat poukaz? Kód přestane fungovat.")) return;
    await adminApi.delete(`/vouchers/${v.id}`);
    reload();
  };
  const resend = () => adminApi.post(`/vouchers/${v.id}/resend`).then((r) => (r.data.sent ? toast.success("E-mail s poukazem odeslán") : toast.error("Odeslání selhalo"))).catch((e) => toast.error(errMsg(e)));
  const expired = v.valid_until < new Date().toISOString().slice(0, 10);
  return (
    <div className="flex flex-wrap items-end gap-3 rounded-3xl border bg-white p-4" data-testid={`admin-voucher-${i}`}>
      <div className="min-w-[160px]">
        <p className="font-mono text-base font-bold tracking-wider" data-testid={`voucher-code-${i}`}>{v.code}</p>
        <p className="text-xs text-[#6E7565]">{v.order_number ? `Obj. ${v.order_number} · ${v.buyer_email}` : v.note || "Vytvořeno ručně"}</p>
        <p className="text-xs text-[#6E7565]">Vytvořeno {fmtDate(v.created_at)}{v.redemptions?.length ? ` · použito ${v.redemptions.length}×` : ""}</p>
      </div>
      <p className="pb-2 text-sm">Hodnota <b>{czk(v.value)}</b></p>
      <Field label="Zůstatek Kč" type="number" className="w-28" value={v.balance} onChange={(e) => patch(v.id, "balance", e.target.value)} testid={`voucher-balance-${i}`} />
      <Field label="Platí do" type="date" className="w-40" value={v.valid_until || ""} onChange={(e) => patch(v.id, "valid_until", e.target.value)} testid={`voucher-until-${i}`} />
      <div className="pb-2"><Toggle label={expired ? "Aktivní (prošlý)" : "Aktivní"} checked={!!v.active} onChange={(x) => patch(v.id, "active", x)} testid={`voucher-active-${i}`} /></div>
      <div className="ml-auto flex flex-wrap gap-2">
        <button onClick={() => preview(v)} className="btn-ghost !px-4 !py-2" data-testid={`voucher-preview-${i}`}><Eye className="h-4 w-4" /> Náhled / tisk</button>
        {v.order_id && <button onClick={resend} className="btn-ghost !px-4 !py-2" data-testid={`voucher-resend-${i}`}><Mail className="h-4 w-4" /></button>}
        <button onClick={save} className="btn-sage !px-4 !py-2" data-testid={`voucher-save-${i}`}><Save className="h-4 w-4" /> Uložit</button>
        <button onClick={remove} className="rounded-full p-2 text-[#D96B43] hover:bg-[#F8E1D7]" data-testid={`voucher-delete-${i}`}><Trash2 className="h-4 w-4" /></button>
      </div>
    </div>
  );
};

export function Vouchers() {
  const [list, load, setList] = useList("/vouchers");
  const [value, setValue] = useState(500);
  const [note, setNote] = useState("");
  const [preview, setPreview] = useState(null);
  const patch = (id, k, v) => setList(list.map((x) => (x.id === id ? { ...x, [k]: v } : x)));
  const create = async (e) => {
    e.preventDefault();
    try {
      await adminApi.post("/vouchers", { value: Number(value), note });
      setNote("");
      toast.success("Poukaz vytvořen");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <section className="mt-12" data-testid="admin-vouchers">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div><h2 className="h-display text-2xl sm:text-3xl">Dárkové poukazy</h2><p className="mt-1 text-sm text-[#6E7565]">Kódy se vytvoří automaticky po zaplacení objednávky poukazu. Zůstatek se odečítá při každém použití.</p></div>
        <button className="btn-ghost" onClick={() => setPreview({ value, code: "HM00000-UKAZK", valid_until: "" })} data-testid="voucher-design-preview-btn"><Eye className="h-4 w-4" /> Náhled vzhledu</button>
      </div>
      <form onSubmit={create} className="mb-5 flex flex-wrap items-end gap-2 rounded-3xl bg-[#E9EFE0] p-4">
        <label className="text-sm"><span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-[#6E7565]">Hodnota</span>
          <select className="input-soft" value={value} onChange={(e) => setValue(e.target.value)} data-testid="voucher-new-value">{[100, 200, 300, 500, 1000].map((v) => <option key={v} value={v}>{v} Kč</option>)}</select></label>
        <Field label="Poznámka (komu, proč)" className="min-w-[200px] flex-1" value={note} onChange={(e) => setNote(e.target.value)} testid="voucher-new-note" />
        <button className="btn-sage" data-testid="voucher-create-btn"><Plus className="h-4 w-4" /> Vytvořit poukaz ručně</button>
      </form>
      <div className="space-y-3">
        {(list || []).map((v, i) => <Row key={v.id} v={v} i={i} patch={patch} reload={load} preview={setPreview} />)}
        {list && !list.length && <p className="text-sm text-[#6E7565]" data-testid="vouchers-empty">Zatím nebyl prodán žádný dárkový poukaz.</p>}
      </div>
      {preview && <PrintModal v={preview} onClose={() => setPreview(null)} />}
    </section>
  );
}
